import type { ShipTime } from '@felix/domain';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Tabs,
  Textarea,
} from '@mantine/core';
import { IconAlertCircle, IconFileImport, IconGauge } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { DateTimeInput } from '../../inputs/DateTimeInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { zoneOffsetMinutes } from '../personnel/rest-hours.js';
import { useHours } from './ComponentDetails.js';
import {
  type CounterFormValues,
  confirmationKey,
  confirmationParams,
  emptyCounterForm,
  type HoursField,
  hoursInputFormat,
  requiredConfirmations,
  toReadingInput,
  utcToShipLocal,
  withSuggestion,
} from './counter-form.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import classes from './Pms.module.css';
import type { CounterEditor } from './source.js';
import type { CounterInfo, CounterSuggestion, EquipmentDetail, Reading } from './types.js';

export interface CounterDialogProps {
  opened: boolean;
  counter: CounterInfo | null;
  timeZone: ShipTime;
  editor: CounterEditor;
  now: () => Date;
  onClose: () => void;
  onSaved: (result: { reading: Reading; equipment: EquipmentDetail }) => void;
}

/**
 * Sayaç diyaloğu (yalnız gemide; K13): sayaç değeri ya da son okumadan bu yana; "Replace meter"
 * sekmesi eski son değeri, yeni başlangıcı ve gerekçeyi ister (K4); Noon Report önerisi bir
 * düğmeyle doldurulur, yazmaz (K2). [NR496 4.5.1c, 4.5.1d] onayları sunucu ister; ayrı pencerede
 * tek tek işaretlenmeden kayıt oluşmaz.
 */
export function CounterDialog({
  opened,
  counter,
  timeZone,
  editor,
  now,
  onClose,
  onSaved,
}: CounterDialogProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) => t(`pms.counter.${key}`, values);
  const hours = useHours();
  const errorText = usePmsErrorText();
  const [values, setValues] = useState<CounterFormValues>(() =>
    emptyCounterForm(utcToShipLocal(now(), timeZone)),
  );
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState<CounterSuggestion | null | undefined>(undefined);
  const [required, setRequired] = useState<string[]>([]);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  useUnsavedChanges(opened && touched);

  const equipmentId = counter?.equipmentId ?? null;
  useEffect(() => {
    if (!opened || !equipmentId) return;
    setValues(emptyCounterForm(utcToShipLocal(now(), timeZone)));
    setTouched(false);
    setError(null);
    setRequired([]);
    setTicked(new Set());
    setSuggestion(undefined);
    let live = true;
    editor
      .suggestion(equipmentId)
      .then((value) => {
        if (live) setSuggestion(value);
      })
      .catch(() => {
        if (live) setSuggestion(null);
      });
    return () => {
      live = false;
    };
  }, [opened, equipmentId, editor, now, timeZone]);

  const set = <K extends keyof CounterFormValues>(key: K, value: CounterFormValues[K]) => {
    setTouched(true);
    setValues((current) => ({ ...current, [key]: value }));
  };
  const hoursValue = (value: string | number): HoursField =>
    typeof value === 'number' ? value : '';
  const missing = (value: HoursField | string) => touched && error === 'MISSING' && value === '';
  const requiredText = t('pms.fieldErrors.REQUIRED');
  const maxDate = utcToShipLocal(now(), timeZone);
  const last = counter?.lastReading ?? null;
  // Sayı biçimi dile göre: önceki okumayla aynı ayraçlar (M7A-SCR-5)
  const numberFormat = hoursInputFormat(i18n.resolvedLanguage);

  const save = async (confirmations: readonly string[]) => {
    if (!equipmentId) return;
    const input = toReadingInput(values, { counter, timeZone, confirmations });
    if (!input || (values.tab === 'replace' && values.reason.trim() === '')) {
      setTouched(true);
      setError('MISSING');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await editor.addReading(equipmentId, input);
      setRequired([]);
      setTouched(false);
      onSaved(result);
    } catch (failure) {
      const asked = requiredConfirmations(failure);
      if (asked.length > 0) {
        setRequired(asked);
        setTicked(new Set());
      } else {
        setError(failure);
      }
    } finally {
      setBusy(false);
    }
  };

  const reading = toReadingInput(values, { counter, timeZone, confirmations: [] });
  const params = reading
    ? confirmationParams(reading, counter, i18n.resolvedLanguage ?? 'en')
    : { previous: '—', value: '—', perDay: '—', limit: String(counter?.maxDailyHours ?? 24) };
  const readingKind = values.tab === 'replace' ? values.kind : 'reading';

  return (
    <>
      <Modal
        opened={opened && required.length === 0}
        onClose={onClose}
        size="lg"
        title={
          <span className={classes.modalTitle}>
            <IconGauge size={20} aria-hidden />
            {k('title')}
          </span>
        }
        closeButtonProps={{ 'aria-label': k('close') }}
      >
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void save([]);
          }}
        >
          <Stack gap="sm">
            {counter ? (
              <p className={classes.modalIntro}>
                {k('subtitle', { name: counter.equipmentName, limit: counter.maxDailyHours })}
              </p>
            ) : null}
            <div className={classes.previous}>
              <span>{k('previous')}</span>
              <strong>
                {last
                  ? k('previousValue', {
                      hours: hours(last.totalHours),
                      date: formatDateTime(last.readAt, last.tzOffsetMin),
                    })
                  : k('previousNone')}
              </strong>
            </div>
            {error && error !== 'MISSING' ? (
              <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
                {errorText(error)}
              </Alert>
            ) : null}
            <Tabs
              value={values.tab}
              onChange={(tab) => set('tab', tab === 'replace' ? 'replace' : 'reading')}
            >
              <Tabs.List>
                <Tabs.Tab value="reading">{k('tabs.reading')}</Tabs.Tab>
                <Tabs.Tab value="replace">{k('tabs.replace')}</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="reading" pt="sm">
                <Stack gap="sm">
                  <SegmentedControl
                    aria-label={k('mode')}
                    value={values.mode}
                    onChange={(mode) => set('mode', mode === 'delta' ? 'delta' : 'total')}
                    data={[
                      { value: 'total', label: k('modeTotal') },
                      { value: 'delta', label: k('modeDelta') },
                    ]}
                  />
                  {values.mode === 'total' ? (
                    <NumberInput
                      label={k('totalHours')}
                      required
                      min={0}
                      decimalScale={1}
                      {...numberFormat}
                      value={values.totalHours}
                      onChange={(value) => set('totalHours', hoursValue(value))}
                      error={missing(values.totalHours) ? requiredText : undefined}
                    />
                  ) : (
                    <>
                      <NumberInput
                        label={k('deltaHours')}
                        required
                        min={0}
                        decimalScale={1}
                        {...numberFormat}
                        value={values.deltaHours}
                        onChange={(value) => set('deltaHours', hoursValue(value))}
                        error={missing(values.deltaHours) ? requiredText : undefined}
                      />
                      {reading ? (
                        <span className={classes.result}>
                          {k('result', { hours: hours(reading.totalHours) })}
                        </span>
                      ) : null}
                    </>
                  )}
                  {suggestion ? (
                    <Group gap="xs">
                      <Button
                        variant="light"
                        size="xs"
                        leftSection={<IconFileImport size={14} aria-hidden />}
                        onClick={() => {
                          setTouched(true);
                          setValues((current) => withSuggestion(current, suggestion, timeZone));
                        }}
                      >
                        {k('suggestion', { hours: hours(suggestion.hours) })}
                      </Button>
                      <span className={classes.muted}>
                        {k('suggestionHint', {
                          date: formatDateTime(
                            suggestion.reportedAt,
                            zoneOffsetMinutes(timeZone, Date.parse(suggestion.reportedAt)),
                          ),
                        })}
                      </span>
                    </Group>
                  ) : suggestion === null ? (
                    <span className={classes.muted}>{k('noSuggestion')}</span>
                  ) : null}
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="replace" pt="sm">
                <Stack gap="sm">
                  <SegmentedControl
                    aria-label={k('eventKind')}
                    value={values.kind}
                    onChange={(kind) =>
                      set('kind', kind === 'meter_reset' ? 'meter_reset' : 'meter_replaced')
                    }
                    data={[
                      { value: 'meter_replaced', label: t('pms.readingKinds.meter_replaced') },
                      { value: 'meter_reset', label: t('pms.readingKinds.meter_reset') },
                    ]}
                  />
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    <NumberInput
                      label={k('previousMeterFinal')}
                      required
                      min={0}
                      decimalScale={1}
                      {...numberFormat}
                      value={values.previousMeterFinal}
                      onChange={(value) => set('previousMeterFinal', hoursValue(value))}
                      error={missing(values.previousMeterFinal) ? requiredText : undefined}
                    />
                    <NumberInput
                      label={k('meterStartHours')}
                      required
                      min={0}
                      decimalScale={1}
                      {...numberFormat}
                      value={values.meterStartHours}
                      onChange={(value) => set('meterStartHours', hoursValue(value))}
                      error={missing(values.meterStartHours) ? requiredText : undefined}
                    />
                  </SimpleGrid>
                  <Textarea
                    label={k('reason')}
                    required
                    autosize
                    minRows={2}
                    maxLength={500}
                    value={values.reason}
                    onChange={(event) => set('reason', event.currentTarget.value)}
                    error={
                      touched && error === 'MISSING' && values.reason.trim() === ''
                        ? requiredText
                        : undefined
                    }
                  />
                </Stack>
              </Tabs.Panel>
            </Tabs>
            <DateTimeInput
              label={k('readAt')}
              required
              value={values.readAt}
              maxDate={maxDate}
              onChange={(value) => set('readAt', value)}
              error={missing(values.readAt) ? requiredText : undefined}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={onClose}>
                {k('cancel')}
              </Button>
              <Button type="submit" loading={busy}>
                {k('save')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* [NR496 4.5.1c, 4.5.1d] onayları ayrı pencerede, her biri ayrı işaretlenir (K5) */}
      <Modal
        opened={opened && required.length > 0}
        onClose={() => setRequired([])}
        title={k('confirmTitle')}
        closeButtonProps={{ 'aria-label': k('back') }}
      >
        <Stack gap="sm">
          <p className={classes.modalIntro}>{k('confirmIntro')}</p>
          {required.map((code) => (
            <Checkbox
              key={code}
              label={t(confirmationKey(code, readingKind), params)}
              checked={ticked.has(code)}
              onChange={(event) => {
                const next = new Set(ticked);
                if (event.currentTarget.checked) next.add(code);
                else next.delete(code);
                setTicked(next);
              }}
            />
          ))}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setRequired([])}>
              {k('back')}
            </Button>
            <Button
              loading={busy}
              disabled={!required.every((code) => ticked.has(code))}
              onClick={() => void save(required)}
            >
              {k('confirmSave')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
